import { useCallback, useEffect, useState } from 'react';

import { versionsApi, WEIGHT_MODES } from '../../api';
import {
  Badge,
  Button,
  EmptyState,
  Loading,
  Modal,
  Select,
  Switch,
  TextArea,
  TextInput,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useModel } from '../../context/ModelContext';
import { useToast } from '../../context/ToastContext';
import { useAttributeSets } from '../../hooks/useAttributeSet';
import { formatBytes, formatMetric, relativeTime } from '../../utils/format';
import '../pages.css';

const SCHEMES = ['standard', 'indexed', 'json'];
const DETAIL_LEVELS = ['attr_desc_vals', 'attr_vals', 'attr', 'none'];
const SERVING_WEIGHT_OPTIONS = [
  { value: 'default', label: 'Serving default (Settings → Model serving)' },
  ...WEIGHT_MODES.map((mode) => ({ value: mode.value, label: mode.label })),
];
const SERVING_BADGES = {
  ready: { tone: 'ok', label: 'serving' },
  starting: { tone: 'warn', label: 'loading…' },
  draining: { tone: 'warn', label: 'unloading…' },
  failed: { tone: 'danger', label: 'load failed' },
  unknown: { tone: 'danger', label: 'model server offline' },
  invalid: { tone: 'danger', label: 'cannot be served' },
};

/**
 * Model version management.
 *
 * The metadata editor exists because a checkpoint records its base model and LoRA
 * hyper-parameters but not the three things that decide whether its output can be
 * read: the attribute set, the response scheme and the image preprocessing. Get
 * those wrong and nothing errors — the parser just quietly mis-reads the answer.
 */

/**
 * Padlock icons, drawn rather than borrowed.
 *
 * Inline SVG inherits `currentColor`, so each one takes the colour of the button it
 * sits in and stays legible when the theme flips. An emoji padlock would render in
 * whatever the platform decides and ignore the theme entirely.
 */
function LockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function UnlockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 4.9-.6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function VersionsTab() {
  const toast = useToast();
  const {
    versions,
    activeId,
    loading,
    switching,
    refreshVersions,
    selectVersion,
    unloadModel,
    setVersionLocked,
    patchVersion,
    isVllm,
  } = useModel();
  const { isAdmin } = useAuth();
  const { sets } = useAttributeSets();

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importable, setImportable] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(null);
      return;
    }
    setDraft({
      label: editing.label || '',
      attribute_set: editing.attribute_set || '',
      response_scheme: editing.response_scheme || 'standard',
      detail_level: editing.detail_level || 'attr_desc_vals',
      max_response_tokens: editing.max_response_tokens || 256,
      chunk_size: editing.chunk_size ?? '',
      load_in_4bit: editing.load_in_4bit !== false,
      serving_weights: editing.serving_weights || 'default',
      input_width: editing.preprocessing?.input_size?.[0] ?? 384,
      input_height: editing.preprocessing?.input_size?.[1] ?? 384,
      vertical_anchor: editing.preprocessing?.vertical_anchor || 'bottom',
      upsampling_factor: editing.preprocessing?.upsampling_factor ?? 1,
      notes: editing.notes || '',
    });
  }, [editing]);

  const save = useCallback(async () => {
    if (!editing || !draft) return;
    setSaving(true);
    try {
      await patchVersion(editing.id, {
        label: draft.label,
        attribute_set: draft.attribute_set,
        response_scheme: draft.response_scheme,
        detail_level: draft.detail_level,
        max_response_tokens: Number(draft.max_response_tokens),
        chunk_size: draft.chunk_size === '' ? null : Number(draft.chunk_size),
        load_in_4bit: draft.load_in_4bit,
        serving_weights: draft.serving_weights,
        preprocessing: {
          input_size: [Number(draft.input_width), Number(draft.input_height)],
          vertical_anchor: draft.vertical_anchor,
          upsampling_factor: Number(draft.upsampling_factor),
        },
        notes: draft.notes,
      });
      await refreshVersions();
      toast.success('Metadata saved', editing.id);
      setEditing(null);
    } catch (cause) {
      toast.error(cause, { title: 'Could not save the manifest' });
    } finally {
      setSaving(false);
    }
  }, [draft, editing, patchVersion, refreshVersions, toast]);

  const openImport = useCallback(async () => {
    setImportOpen(true);
    try {
      setImportable(await versionsApi.importable());
    } catch (cause) {
      toast.error(cause, { title: 'Could not scan the experiments directory' });
      setImportable([]);
    }
  }, [toast]);

  if (loading) return <Loading label="Scanning checkpoints…" large />;

  return (
    <>
      <div className="row" style={{ marginBottom: 14 }}>
        <span className="muted small">
          {versions.length} version{versions.length === 1 ? '' : 's'} in the project checkpoints
          folder
        </span>
        <div className="grow" />
        {!isVllm && activeId && (
          <Button
            size="sm"
            variant="ghost"
            loading={switching}
            title="Drop the base model and hand the VRAM back to other processes. The next request reloads it."
            onClick={() => unloadModel({ releaseBase: true })}
          >
            Unload &amp; free GPU
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={refreshVersions}>
          Rescan
        </Button>
        <Button size="sm" onClick={openImport}>
          Import from training
        </Button>
      </div>

      {versions.length === 0 ? (
        <EmptyState glyph="◧" title="No checkpoints found">
          Import a trained checkpoint with{' '}
          <span className="mono">scripts/import_checkpoint.py</span>, or copy a{' '}
          <span className="mono">model_state.pth</span> into{' '}
          <span className="mono">checkpoints/&lt;version-id&gt;/</span> and add its metadata here.
          Verify it with <span className="mono">scripts/verify_checkpoint.py</span> before serving.
        </EmptyState>
      ) : (
        <div className="version-grid">
          {versions.map((version) => {
            const serving = isVllm ? version.serving || {} : null;
            const servingBadge = serving ? SERVING_BADGES[serving.state] : null;
            const served = serving?.state === 'ready';
            return (
              <article
                key={version.id}
                className="card card--pad"
                style={
                  version.id === activeId
                    ? {
                        borderColor: 'var(--accent-line)',
                        boxShadow: 'inset 0 0 0 1px var(--accent-soft)',
                      }
                    : undefined
                }
              >
                <div className="row" style={{ marginBottom: 3 }}>
                  <strong>{version.label || version.id}</strong>
                  {version.id === activeId && (
                    <Badge tone="ok">{isVllm ? 'selected' : 'loaded'}</Badge>
                  )}
                  {servingBadge && (
                    <Badge tone={servingBadge.tone} title={serving.error || undefined}>
                      {servingBadge.label}
                    </Badge>
                  )}
                  {version.is_locked && (
                    <Badge tone="warn">
                      <LockIcon /> locked
                    </Badge>
                  )}
                  {version.base_only && <Badge>base · untuned</Badge>}
                  {version.model_family && version.model_family !== 'qwen3vl' && (
                    <Badge>{version.model_family}</Badge>
                  )}
                  {!version.is_ready && <Badge tone="warn">needs metadata</Badge>}
                </div>
                <div className="xs muted" style={{ marginBottom: 10 }}>
                  {version.id} · {formatBytes(version.size_bytes)} ·{' '}
                  {relativeTime(new Date(version.modified_at * 1000).toISOString())}
                </div>

                <div className="kv">
                  <span className="kv__key">Base model</span>
                  <span className="kv__value mono">{version.base_model_id.split('/').pop()}</span>
                </div>
                <div className="kv">
                  <span className="kv__key">LoRA</span>
                  <span className="kv__value">
                    {version.base_only ? (
                      <span className="muted">none — untuned base model</span>
                    ) : (
                      <>
                        r {version.lora.r} · α {version.lora.alpha}
                      </>
                    )}
                  </span>
                </div>
                {serving && serving.weights_mode && (
                  <div className="kv">
                    <span className="kv__key">Serving</span>
                    <span className="kv__value">
                      {serving.weights_label}
                      {version.serving_weights && version.serving_weights !== 'default'
                        ? ' (this version)'
                        : ' (default)'}
                      {serving.prepared === false && (
                        <span style={{ color: 'var(--state-warn)' }}> · not prepared</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="kv">
                  <span className="kv__key">Attribute set</span>
                  <span className="kv__value">
                    {version.attribute_set || (
                      <span style={{ color: 'var(--state-warn)' }}>not set</span>
                    )}
                  </span>
                </div>
                <div className="kv">
                  <span className="kv__key">Scheme</span>
                  <span className="kv__value">
                    {version.response_scheme} · {version.detail_level}
                  </span>
                </div>
                <div className="kv">
                  <span className="kv__key">Preprocessing</span>
                  <span className="kv__value">
                    {(version.preprocessing?.input_size || []).join('×') || '—'} ·{' '}
                    {version.preprocessing?.vertical_anchor || 'bottom'}
                    {version.preprocessing?.upsampling_factor > 1
                      ? ` · ${version.preprocessing.upsampling_factor}×`
                      : ''}
                  </span>
                </div>
                <div className="kv">
                  <span className="kv__key">{version.training?.metric_name || 'Metric'}</span>
                  <span className="kv__value">
                    {formatMetric(version.training?.metric_value)}
                    {version.training?.epoch !== null && version.training?.epoch !== undefined
                      ? ` · epoch ${version.training.epoch}`
                      : ''}
                  </span>
                </div>

                {version.manifest_errors?.length > 0 && (
                  <div className="xs" style={{ color: 'var(--state-warn)', marginTop: 8 }}>
                    {version.manifest_errors.map((problem) => (
                      <div key={problem}>• {problem}</div>
                    ))}
                  </div>
                )}

                <div className="row" style={{ marginTop: 12 }}>
                  <Button size="sm" onClick={() => setEditing(version)}>
                    {version.is_ready ? 'Edit metadata' : 'Add metadata'}
                  </Button>
                  {isVllm ? (
                    <>
                      {served && version.id !== activeId && (
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={!version.is_ready || version.is_locked}
                          onClick={() => selectVersion(version.id)}
                        >
                          Use
                        </Button>
                      )}
                      {!served && isAdmin && (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={switching}
                          disabled={
                            !version.is_ready ||
                            version.is_locked ||
                            ['starting', 'draining'].includes(serving.state)
                          }
                          title={`Loads ${serving.model_source || 'its base model'} on the GPU, under the serving policy.`}
                          onClick={() => selectVersion(version.id)}
                        >
                          Load
                        </Button>
                      )}
                      {served && isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={switching}
                          title="Unloads the base model for every version that runs on it."
                          onClick={() => unloadModel({ versionId: version.id })}
                        >
                          Unload base model
                        </Button>
                      )}
                    </>
                  ) : version.id === activeId ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={switching}
                      title="Detach this adapter. The base model stays resident, so reloading is fast."
                      onClick={() => unloadModel({ releaseBase: false })}
                    >
                      Unload adapter
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!version.is_ready || version.is_locked}
                      title={
                        version.is_locked
                          ? 'Locked by an administrator. Unlock it before loading.'
                          : undefined
                      }
                      onClick={() => selectVersion(version.id)}
                    >
                      Load
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={version.is_locked ? <UnlockIcon /> : <LockIcon />}
                      title={
                        version.is_locked
                          ? 'Allow this model to be loaded and used again'
                          : 'Stop this model being loaded or used by anyone'
                      }
                      onClick={() => setVersionLocked(version.id, !version.is_locked)}
                    >
                      {version.is_locked ? 'Unlock' : 'Lock'}
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ------------------------------------------------------ metadata editor */}
      <Modal
        open={Boolean(editing && draft)}
        onClose={() => setEditing(null)}
        title={`Metadata · ${editing?.id || ''}`}
        subtitle="These fields are not stored in the checkpoint — they must match how it was trained."
        wide
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              Save metadata
            </Button>
          </>
        }
      >
        {draft && (
          <div className="editor">
            <TextInput
              label="LABEL"
              value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
              placeholder="BiH · 41 attributes · epoch 8"
            />
            <div className="editor__row">
              <Select
                label="ATTRIBUTE SET"
                value={draft.attribute_set}
                onChange={(event) => setDraft({ ...draft, attribute_set: event.target.value })}
                placeholder="Select the vocabulary used in training"
                options={sets.map((set) => ({
                  value: set.id,
                  label: `${set.label} (${set.attribute_count})`,
                }))}
                hint="The order and value strings must be the ones the model was fine-tuned on."
              />
              <Select
                label="RESPONSE SCHEME"
                value={draft.response_scheme}
                onChange={(event) => setDraft({ ...draft, response_scheme: event.target.value })}
                options={SCHEMES.map((scheme) => ({ value: scheme, label: scheme }))}
                hint="standard = numbered “N: Value” lines."
              />
            </div>
            <div className="editor__row">
              <Select
                label="PROMPT DETAIL LEVEL"
                value={draft.detail_level}
                onChange={(event) => setDraft({ ...draft, detail_level: event.target.value })}
                options={DETAIL_LEVELS.map((level) => ({ value: level, label: level }))}
              />
              <TextInput
                label="MAX RESPONSE TOKENS"
                type="number"
                min={16}
                max={4096}
                value={draft.max_response_tokens}
                onChange={(event) =>
                  setDraft({ ...draft, max_response_tokens: event.target.value })
                }
                hint="256 fits 41 numbered answer lines."
              />
            </div>
            <div className="editor__row">
              <TextInput
                label="CHUNK SIZE (OPTIONAL)"
                type="number"
                min={1}
                value={draft.chunk_size}
                onChange={(event) => setDraft({ ...draft, chunk_size: event.target.value })}
                placeholder="empty = one prompt for all attributes"
                hint="Leave empty. Training used a single prompt; chunking sends prompts the model never saw."
              />
              <div className="col" style={{ justifyContent: 'center' }}>
                <Switch
                  checked={draft.load_in_4bit}
                  onChange={(value) => setDraft({ ...draft, load_in_4bit: value })}
                  label="4-bit NF4 base weights (QLoRA)"
                />
                <span className="field__hint">
                  Training used 4-bit by default. Changing this changes the numerics.
                </span>
              </div>
            </div>

            <Select
              label="SERVING WEIGHTS (vLLM)"
              value={draft.serving_weights}
              onChange={(event) => setDraft({ ...draft, serving_weights: event.target.value })}
              options={SERVING_WEIGHT_OPTIONS}
              hint={
                WEIGHT_MODES.find((mode) => mode.value === draft.serving_weights)?.hint ||
                'Use the administrator’s default from Settings → Model serving. Only applies with INFERENCE_BACKEND=vllm.'
              }
            />

            <div className="section-label">Image preprocessing</div>
            <div className="editor__row">
              <TextInput
                label="INPUT WIDTH"
                type="number"
                value={draft.input_width}
                onChange={(event) => setDraft({ ...draft, input_width: event.target.value })}
              />
              <TextInput
                label="INPUT HEIGHT"
                type="number"
                value={draft.input_height}
                onChange={(event) => setDraft({ ...draft, input_height: event.target.value })}
              />
            </div>
            <div className="editor__row">
              <Select
                label="CROP ANCHOR"
                value={draft.vertical_anchor}
                onChange={(event) => setDraft({ ...draft, vertical_anchor: event.target.value })}
                options={[
                  { value: 'bottom', label: 'bottom (training default — keeps the road)' },
                  { value: 'center', label: 'center' },
                  { value: 'top', label: 'top' },
                ]}
              />
              <TextInput
                label="UPSAMPLING FACTOR"
                type="number"
                step="0.5"
                min="0.5"
                max="4"
                value={draft.upsampling_factor}
                onChange={(event) => setDraft({ ...draft, upsampling_factor: event.target.value })}
                hint="Must match training, or the vision-token count differs."
              />
            </div>
            <TextArea
              label="NOTES"
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              placeholder="Which experiment this came from, what it is for…"
              style={{ minHeight: 70 }}
            />
          </div>
        )}
      </Modal>

      {/* -------------------------------------------------------------- import */}
      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import from the training experiments tree"
        subtitle="Set EXPERIMENTS_DIR to your $VIDLU_DATA/experiments/states path to enable this."
        wide
      >
        {importable === null ? (
          <Loading label="Scanning…" />
        ) : importable.length === 0 ? (
          <EmptyState glyph="◫" title="Nothing found">
            No <span className="mono">model_state.pth</span> under the configured experiments
            directory. You can also copy one in manually, or run{' '}
            <span className="mono">scripts/import_checkpoint.py --scan &lt;path&gt;</span>.
          </EmptyState>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Suggested id</th>
                <th>Epoch</th>
                <th>Perf</th>
                <th>Size</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {importable.map((item) => (
                <tr key={item.checkpoint_path}>
                  <td className="mono">{item.suggested_id}</td>
                  <td>{item.epoch ?? '—'}</td>
                  <td>{formatMetric(item.perf)}</td>
                  <td>{formatBytes(item.size_bytes)}</td>
                  <td>
                    {item.already_imported ? (
                      <Badge>imported</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await versionsApi.import({
                              source: item.checkpoint_path,
                              version_id: item.suggested_id,
                              training: {
                                epoch: item.epoch,
                                metric_value: item.perf,
                                experiment_dir: item.experiment,
                              },
                            });
                            await refreshVersions();
                            toast.success(
                              'Checkpoint imported',
                              'Add its attribute set to make it usable.',
                            );
                            setImportOpen(false);
                          } catch (cause) {
                            toast.error(cause, { title: 'Import failed' });
                          }
                        }}
                      >
                        Import
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </>
  );
}
